import React, { useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import CustomTabs from "../../common/custom-tabs";
import typography from "../../cssUtils/typography";
import PolicyTable from "./PolicyTable";
import InsuranceUploadTab from "./InsuranceUploadTab";
import { formatWithTzLabel } from "../../utils/dateUtils";
import {
  INSURANCE_HEALTH_ENDPOINT,
  INSURANCE_GENERAL_ENDPOINT,
  INSURANCE_LIFE_ENDPOINT,
} from "../../config/urls";

const TABS = [
  { value: "health", label: "Health Insurance" },
  { value: "general", label: "General Insurance" },
  { value: "life", label: "Life Insurance" },
  { value: "upload", label: "Upload" },
];

const currency = (value) => (value == null || value === "" ? "-" : `₹${value}`);
const formatDateTime = (value) => (value ? formatWithTzLabel(value) : "-");

const REMINDER_STATUS_LABELS = {
  reminder_1: "Reminder-1",
  reminder_2: "Reminder-2",
  reminder_3: "Reminder-3",
  reminder_4: "Reminder-4",
  reminder_5: "Reminder-5",
};

const STATUS_CHIP_COLOR = {
  yes: "success",
  no: "error",
  reminder_1: "info",
  reminder_2: "info",
  reminder_3: "info",
  reminder_4: "info",
  reminder_5: "info",
};

const renderStatusChip = (value, labelMap = {}) => {
  if (!value) return <Typography sx={typography.bodyLarge}>-</Typography>;
  return (
    <Chip
      size="small"
      variant="outlined"
      color={STATUS_CHIP_COLOR[value] || "default"}
      label={labelMap[value] || value}
    />
  );
};

const HEALTH_COLUMNS = [
  { id: "customer_name", label: "Customer Name" },
  {
    id: "country_code",
    label: "Code",
    render: (row) => <Typography sx={typography.bodyLarge}>+{row.country_code}</Typography>,
  },
  { id: "phone_number", label: "Phone Number" },
  { id: "policy_number", label: "Policy No." },
  { id: "policy_name", label: "Policy" },
  {
    id: "premium_amount",
    label: "Premium",
    align: "right",
    render: (row) => <Typography sx={typography.bodyLarge}>{currency(row.premium_amount)}</Typography>,
  },
  { id: "frequency", label: "Frequency" },
  { id: "expiry_date", label: "Expiry Date" },
  {
    id: "reminder_status",
    label: "Reminder Status",
    render: (row) => renderStatusChip(row.reminder_status, REMINDER_STATUS_LABELS),
  },
  {
    id: "customer_response",
    label: "Customer Response",
    render: (row) => renderStatusChip(row.customer_response),
  },
  {
    id: "last_reminder_sent",
    label: "Last Reminder Sent",
    render: (row) => (
      <Typography sx={typography.bodyLarge}>{formatDateTime(row.last_reminder_sent)}</Typography>
    ),
  },
];

const GENERAL_COLUMNS = [
  { id: "customer_name", label: "Customer Name" },
  {
    id: "country_code",
    label: "Code",
    render: (row) => <Typography sx={typography.bodyLarge}>+{row.country_code}</Typography>,
  },
  { id: "phone_number", label: "Phone Number" },
  { id: "email", label: "Email" },
  { id: "insurer", label: "Insurer" },
  { id: "policy_number", label: "Policy No." },
  { id: "policy_type", label: "Policy Type" },
  { id: "frequency", label: "Frequency" },
  { id: "expiry_date", label: "Expiry Date" },
  {
    id: "reminder_status",
    label: "Reminder Status",
    render: (row) => renderStatusChip(row.reminder_status, REMINDER_STATUS_LABELS),
  },
  {
    id: "customer_response",
    label: "Customer Response",
    render: (row) => renderStatusChip(row.customer_response),
  },
  {
    id: "last_reminder_sent",
    label: "Last Reminder Sent",
    render: (row) => (
      <Typography sx={typography.bodyLarge}>{formatDateTime(row.last_reminder_sent)}</Typography>
    ),
  },
];

const LIFE_COLUMNS = [
  { id: "customer_name", label: "Customer Name" },
  { id: "gender", label: "Gender" },
  { id: "age", label: "Age" },
  {
    id: "country_code",
    label: "Code",
    render: (row) => <Typography sx={typography.bodyLarge}>+{row.country_code}</Typography>,
  },
  { id: "phone_number", label: "Phone Number" },
  { id: "policy_number", label: "Policy No." },
  { id: "plan", label: "Plan" },
  { id: "insurer", label: "Insurer" },
  {
    id: "premium_amount",
    label: "Premium",
    align: "right",
    render: (row) => <Typography sx={typography.bodyLarge}>{currency(row.premium_amount)}</Typography>,
  },
  {
    id: "sum_assured",
    label: "Sum Assured",
    align: "right",
    render: (row) => <Typography sx={typography.bodyLarge}>{currency(row.sum_assured)}</Typography>,
  },
  { id: "frequency", label: "Frequency" },
  { id: "expiry_date", label: "Expiry Date" },
  {
    id: "reminder_status",
    label: "Reminder Status",
    render: (row) => renderStatusChip(row.reminder_status, REMINDER_STATUS_LABELS),
  },
  {
    id: "customer_response",
    label: "Customer Response",
    render: (row) => renderStatusChip(row.customer_response),
  },
  {
    id: "last_reminder_sent",
    label: "Last Reminder Sent",
    render: (row) => (
      <Typography sx={typography.bodyLarge}>{formatDateTime(row.last_reminder_sent)}</Typography>
    ),
  },
];

const InsuranceReminderScreen = () => {
  const [activeTab, setActiveTab] = useState("health");

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={typography.h1}>Insurance Reminders</Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomTabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          tabs={TABS}
          sx={{ maxWidth: 640 }}
        />
      </Box>

      {activeTab === "health" && (
        <PolicyTable
          endpoint={INSURANCE_HEALTH_ENDPOINT}
          columns={HEALTH_COLUMNS}
          searchPlaceholder="Search by customer name or policy number"
          emptyMessage="No health insurance policies found"
        />
      )}

      {activeTab === "general" && (
        <PolicyTable
          endpoint={INSURANCE_GENERAL_ENDPOINT}
          columns={GENERAL_COLUMNS}
          searchPlaceholder="Search by customer name or policy number"
          emptyMessage="No general insurance policies found"
        />
      )}

      {activeTab === "life" && (
        <PolicyTable
          endpoint={INSURANCE_LIFE_ENDPOINT}
          columns={LIFE_COLUMNS}
          searchPlaceholder="Search by customer name or policy number"
          emptyMessage="No life insurance policies found"
        />
      )}

      {activeTab === "upload" && <InsuranceUploadTab />}
    </Box>
  );
};

export default InsuranceReminderScreen;
